import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { If } from "../../shared/components/control-statements/If";

import { Hairline } from "../../shared/components/Hairline";
import { Label } from "../../shared/components/Label";
import { Select } from "../../shared/components/Select";
import { Message } from "@components";
import utc from "dayjs/plugin/utc";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
dayjs.extend(utc);
dayjs.extend(isSameOrAfter);

const intervalTypes = {
  FROM_THE_BEGINNING: "FROM_THE_BEGINNING",
  SPECIFIC_DAY: "SPECIFIC_DAY",
  SPECIFIC_MONTH: "SPECIFIC_MONTH",
  SPECIFIC_YEAR: "SPECIFIC_YEAR",
  CUSTOM_RANGE: "CUSTOM_RANGE",
};

const MAX_YEARS_AGO = 7;
const CURRENT_DATE = new Date();

export default function TimeFilter({
  onUpdateFilters,
  onUpdateFilterValues,
  filterType,
  values,
  allowOnlyMonth,
  allowIntervalAfter,
  onDisableApply,
  hideHours,
  hideSpecificDay,
  hideSpecificYear,
}) {
  const [errorMessage, setErrorMessage] = useState(null);

  const updateFilters = (filters) => {
    onUpdateFilters(filters);
    onUpdateFilterValues(
      { beginValues, endValues, specificValues },
      selectedFilterType
    );
  };

  const [selectedFilterType, setSelectedFilterType] = useState(filterType);
  const [filterValues, setFilterValues] = useState({ begin: null, end: null });

  const [beginValues, setBeginValues] = useState(
    values?.beginValues || { year: null, month: null, day: null, time: null }
  );
  const [endValues, setEndValues] = useState(
    values?.endValues || { year: null, month: null, day: null, time: null }
  );
  const [specificValues, setSpecificValues] = useState(
    values?.specificValues || { year: null, month: null, day: null }
  );

  useEffect(() => {
    let newFilterValues;

    const customDateBegin = dayjs(
      `${beginValues.year}-${beginValues.month}-${beginValues.day} ${
        beginValues.time || beginValues.time
      }`
    );
    const begin = customDateBegin.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

    const customDateEnd = dayjs(
      `${endValues.year}-${endValues.month}-${endValues.day} ${
        endValues.time || endValues.time
      }`
    );
    const end = customDateEnd.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

    newFilterValues = {
      begin,
      end,
    };

    setFilterValues(newFilterValues);
  }, [beginValues, endValues]);

  useEffect(() => {
    onUpdateFilterValues(
      { beginValues, endValues, specificValues },
      selectedFilterType
    );
  }, [beginValues, endValues, specificValues, selectedFilterType]);

  useEffect(() => {
    setErrorMessage(null);
    onDisableApply && onDisableApply(false);

    if (filterType !== selectedFilterType) {
      if (selectedFilterType === intervalTypes.FROM_THE_BEGINNING) {
        setBeginValues({ year: null, month: null, day: null, time: null });
        setEndValues({ year: null, month: null, day: null, time: null });
        setSpecificValues({ year: null, month: null, day: null });
        setFilterValues({ begin: null, end: null });
        updateFilters({ begin: null, end: null });
      }

      if (selectedFilterType === intervalTypes.SPECIFIC_DAY) {
        const now = dayjs();
        const isCurrentMonth =
          now.year() === CURRENT_DATE.getFullYear() &&
          now.month() + 1 === CURRENT_DATE.getMonth() + 1;

        const totalDays = isCurrentMonth
          ? now.date() - 1
          : dayjs(
              `${CURRENT_DATE.getFullYear()}-${CURRENT_DATE.getMonth() + 1}-01`
            ).daysInMonth();

        setBeginValues({ year: null, month: null, day: null, time: null });
        setEndValues({ year: null, month: null, day: null, time: null });
        selectSpecificDay({
          specific: {
            year: CURRENT_DATE.getFullYear(),
            month: CURRENT_DATE.getMonth() + 1,
            day: totalDays,
          },
        });
      }

      if (selectedFilterType === intervalTypes.SPECIFIC_MONTH) {
        setBeginValues({ year: null, month: null, day: null, time: null });
        setEndValues({ year: null, month: null, day: null, time: null });
        selectSpecificMonth({
          specific: {
            year: CURRENT_DATE.getFullYear(),
            month: CURRENT_DATE.getMonth() + 1,
          },
        });
      }

      if (selectedFilterType === intervalTypes.SPECIFIC_YEAR) {
        setBeginValues({ year: null, month: null, day: null, time: null });
        setEndValues({ year: null, month: null, day: null, time: null });
        selectSpecificYear({
          specific: { year: CURRENT_DATE.getFullYear() },
        });
      }

      if (selectedFilterType === intervalTypes.CUSTOM_RANGE) {
        setSpecificValues({ year: null, month: null, day: null });
        selectCustomDate({
          begin: {
            year: CURRENT_DATE.getFullYear(),
            month: CURRENT_DATE.getMonth() + 1,
            day: CURRENT_DATE.getDate(),
            time: "00:00",
          },
          end: {
            year: CURRENT_DATE.getFullYear(),
            month: CURRENT_DATE.getMonth() + 1,
            day: CURRENT_DATE.getDate(),
            time: "23:00",
          },
        });
      }
    }
  }, [selectedFilterType]);

  useEffect(() => {
    if (allowOnlyMonth) setSelectedFilterType(intervalTypes.SPECIFIC_MONTH);
  }, [allowOnlyMonth]);

  const getHoraries = () => {
    const horaries = [];

    for (let i = 0; i <= 23; i++) {
      horaries.push({
        value: `${String(i).padStart(2, "0")}:00`,
        label: `${String(i).padStart(2, "0")}:00`,
      });
    }

    return horaries;
  };

  const getDays = (year, month) => {
    const now = dayjs();

    const normalizedMonth = Number(month);
    const isCurrentMonth =
      now.year() === year && now.month() + 1 === normalizedMonth;

    const totalDays = isCurrentMonth
      ? now.subtract(1, "day").date()
      : dayjs(
          `${year}-${String(normalizedMonth).padStart(2, "0")}-01`
        ).daysInMonth();

    const days = [];

    for (let i = 1; i <= totalDays; i++) {
      days.push({ value: i, label: i });
    }

    return days;
  };

  useEffect(() => {
    if (selectedFilterType === intervalTypes.SPECIFIC_DAY) {
      const { year, month } = specificValues || {};
      const totalDays = dayjs(`${year}-${month}-01`).daysInMonth();

      const currentDay = specificValues?.day || 1;

      const lastDayInMonth = Math.min(currentDay, totalDays);

      selectDate(intervalTypes.SPECIFIC_DAY, {
        specific: {
          day: lastDayInMonth,
        },
      });
    }
  }, [specificValues.month, specificValues.year]);

  const getMonths = () => {
    const months = [
      { label: "Janeiro", value: 1 },
      { label: "Fevereiro", value: 2 },
      { label: "Março", value: 3 },
      { label: "Abril", value: 4 },
      { label: "Maio", value: 5 },
      { label: "Junho", value: 6 },
      { label: "Julho", value: 7 },
      { label: "Agosto", value: 8 },
      { label: "Setembro", value: 9 },
      { label: "Outubro", value: 10 },
      { label: "Novembro", value: 11 },
      { label: "Dezembro", value: 12 },
    ];

    return months;
  };

  const getYears = () => {
    const years = [];

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const minimunYear = currentYear - MAX_YEARS_AGO;

    for (let y = currentYear; y >= minimunYear; y--) {
      years.push({
        value: y,
        label: y,
      });
    }

    return years;
  };

  const selectCustomDate = ({ begin, end }) => {
    let newFilterValues = { ...filterValues };

    if (begin) {
      setBeginValues({
        ...beginValues,
        ...(begin.year ? { year: begin.year } : {}),
        ...(begin.month ? { month: begin.month } : {}),
        ...(begin.day ? { day: begin.day } : {}),
        ...(begin.time ? { time: begin.time } : {}),
      });

      if (
        (begin.time || beginValues.time) &&
        (begin.day || beginValues.day) &&
        (begin.month || beginValues.month) &&
        (begin.year || beginValues.year)
      ) {
        const customDate = dayjs(
          `${begin.year || beginValues.year}-${
            begin.month || beginValues.month
          }-${begin.day || beginValues.day} ${begin.time || beginValues.time}`
        );
        const customDateBegin = customDate.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

        newFilterValues = {
          ...newFilterValues,
          begin: customDateBegin,
        };
      }
    }

    if (end) {
      setEndValues({
        ...endValues,
        ...(end.year ? { year: end.year } : {}),
        ...(end.month ? { month: end.month } : {}),
        ...(end.day ? { day: end.day } : {}),
        ...(end.time ? { time: end.time } : {}),
      });

      if (
        (end.time || endValues.time) &&
        (end.day || endValues.day) &&
        (end.month || endValues.month) &&
        (end.year || endValues.year)
      ) {
        const customDate = dayjs(
          `${end.year || endValues.year}-${end.month || endValues.month}-${
            end.day || endValues.day
          } ${end.time || endValues.time}`
        );
        const customDateEnd = customDate.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

        newFilterValues = {
          ...newFilterValues,
          end: customDateEnd,
        };
      }
    }

    if (!!allowIntervalAfter) {
      const beginDate = dayjs.utc(newFilterValues.begin);
      const endDate = dayjs.utc(newFilterValues.end);
      const minDate = dayjs(allowIntervalAfter);
      if (
        !beginDate.isSameOrAfter(minDate, "day") ||
        !endDate.isSameOrAfter(minDate, "day")
      ) {
        setErrorMessage(
          "Só é possível utilizar o intervalo personalizado para períodos após " +
            minDate.format("DD/MM/YYYY")
        );
        onDisableApply && onDisableApply(true);
      } else {
        onDisableApply && onDisableApply(false);
        setErrorMessage(null);
      }
    }

    setFilterValues(newFilterValues);

    if (newFilterValues.begin && newFilterValues.end) {
      updateFilters(newFilterValues);
    }
  };

  const selectSpecificDay = ({ specific }) => {
    let newFilterValues = { ...filterValues };

    setSpecificValues({
      ...specificValues,
      ...(specific.year ? { year: specific.year } : {}),
      ...(specific.month ? { month: specific.month } : {}),
      ...(specific.day ? { day: specific.day } : {}),
    });

    if (
      (specific.day || specificValues.day) &&
      (specific.month || specificValues.month) &&
      (specific.year || specificValues.year)
    ) {
      const specificDay = dayjs(
        `${specific.year || specificValues.year}-${
          specific.month || specificValues.month
        }-${specific.day || specificValues.day}`
      );
      const specificDayBegin = specificDay.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
      const specificDayEnd = specificDay
        .endOf("day")
        .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

      newFilterValues = {
        ...newFilterValues,
        begin: specificDayBegin,
        end: specificDayEnd,
      };

      setFilterValues(newFilterValues);
      updateFilters(newFilterValues);
    }
  };

  const selectSpecificMonth = ({ specific }) => {
    let newFilterValues = { ...filterValues };

    setSpecificValues({
      ...specificValues,
      ...(specific.year ? { year: specific.year } : {}),
      ...(specific.month ? { month: specific.month } : {}),
    });

    if (
      (specific.month || specificValues.month) &&
      (specific.year || specificValues.year)
    ) {
      const specificMonth = dayjs(
        `${specific.year || specificValues.year}-${
          specific.month || specificValues.month
        }`
      );
      const specificMonthBegin = specificMonth.format(
        "YYYY-MM-DDTHH:mm:ss.SSS[Z]"
      );
      const specificMonthEnd = specificMonth
        .endOf("month")
        .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

      newFilterValues = {
        ...newFilterValues,
        begin: specificMonthBegin,
        end: specificMonthEnd,
      };

      setFilterValues(newFilterValues);
      updateFilters(newFilterValues);
    }
  };

  const selectSpecificYear = ({ specific }) => {
    let newFilterValues = { ...filterValues };

    if (!specific?.year) return;
    setSpecificValues({ ...specificValues, year: specific.year });

    const specificYearBegin = `${specific.year}-01-01T00:00:00.000Z`;
    const specificYearEnd = `${specific.year}-12-31T23:59:59.999Z`;

    newFilterValues = {
      ...newFilterValues,
      begin: specificYearBegin,
      end: specificYearEnd,
    };

    setFilterValues(newFilterValues);
    updateFilters(newFilterValues);
  };

  const selectDate = (dateType, { begin, end, specific }) => {
    switch (dateType) {
      case intervalTypes.SPECIFIC_YEAR:
        selectSpecificYear({ specific });
        break;
      case intervalTypes.SPECIFIC_MONTH:
        selectSpecificMonth({ specific });
        break;
      case intervalTypes.SPECIFIC_DAY:
        selectSpecificDay({ specific });
        break;
      case intervalTypes.CUSTOM_RANGE:
        selectCustomDate({ begin, end });
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Label>Selecione o período</Label>
      <Select
        className="w-100 mb-0"
        value={selectedFilterType}
        options={[
          { label: "Desde o início", value: intervalTypes.FROM_THE_BEGINNING },
          { label: "Dia específico", value: intervalTypes.SPECIFIC_DAY },
          { label: "Mês específico", value: intervalTypes.SPECIFIC_MONTH },
          { label: "Ano específico", value: intervalTypes.SPECIFIC_YEAR },
          {
            label: "Intervalo personalizado",
            value: intervalTypes.CUSTOM_RANGE,
          },
        ].filter((option) => {
          const allowedValues = [];

          if (hideSpecificYear && option.value === intervalTypes.SPECIFIC_YEAR)
            return null;
          if (hideSpecificDay && option.value === intervalTypes.SPECIFIC_DAY)
            return null;

          if (allowOnlyMonth) allowedValues.push(intervalTypes.SPECIFIC_MONTH);

          if (allowIntervalAfter)
            allowedValues.push(intervalTypes.CUSTOM_RANGE);

          if (allowedValues.length === 0) return true;

          return allowedValues.includes(option.value);
        })}
        onChange={(e) => setSelectedFilterType(e.target.value)}
      />

      <If
        cond={
          selectedFilterType === intervalTypes.SPECIFIC_DAY && !hideSpecificDay
        }
      >
        <Hairline className="mt-25 mb-25" />

        <div className="d-flex align-center">
          <Select
            label="Dia"
            className="w-100"
            value={specificValues.day}
            options={getDays(specificValues.year, specificValues.month)}
            onChange={(e) => {
              selectDate(intervalTypes.SPECIFIC_DAY, {
                specific: { day: e.target.value },
              });
            }}
          />
          <Select
            label="Mês"
            className="w-100 ml-15"
            value={specificValues.month}
            options={getMonths()}
            onChange={(e) => {
              selectDate(intervalTypes.SPECIFIC_DAY, {
                specific: { month: e.target.value },
              });
            }}
          />
          <Select
            label="Ano"
            className="w-100 ml-15"
            value={specificValues.year}
            options={getYears()}
            onChange={(e) => {
              selectDate(intervalTypes.SPECIFIC_DAY, {
                specific: { year: e.target.value },
              });
            }}
          />
        </div>
      </If>

      <If cond={selectedFilterType === intervalTypes.SPECIFIC_MONTH}>
        <Hairline className="mt-25 mb-25" />

        <div className="d-flex align-center">
          <Select
            label="Mês"
            className="w-100"
            value={specificValues.month}
            options={getMonths()}
            onChange={(e) => {
              selectDate(intervalTypes.SPECIFIC_MONTH, {
                specific: { month: e.target.value },
              });
            }}
          />
          <Select
            label="Ano"
            className="w-100 ml-15"
            value={specificValues.year}
            options={getYears()}
            onChange={(e) => {
              selectDate(intervalTypes.SPECIFIC_MONTH, {
                specific: { year: e.target.value },
              });
            }}
          />
        </div>
      </If>

      <If
        cond={
          selectedFilterType === intervalTypes.SPECIFIC_YEAR &&
          !hideSpecificYear
        }
      >
        <Hairline className="mt-25 mb-25" />

        <div className="d-flex align-center">
          <Select
            label="Ano"
            className="w-100"
            value={specificValues.year}
            options={getYears()}
            onChange={(e) => {
              selectDate(intervalTypes.SPECIFIC_YEAR, {
                specific: { year: e.target.value },
              });
            }}
          />
        </div>
      </If>

      <If cond={selectedFilterType === intervalTypes.CUSTOM_RANGE}>
        <Hairline className="mt-25 mb-25" />
        <Label bold className="mb-15">
          Início do intervalo em:
        </Label>

        <div className="d-flex align-center">
          <Select
            label="Mês"
            className="w-100 mb-25"
            value={beginValues.month}
            options={getMonths()}
            onChange={(e) => {
              selectDate(intervalTypes.CUSTOM_RANGE, {
                begin: { month: e.target.value },
              });
            }}
          />
          <Select
            label="Ano"
            className="w-100 ml-15 mb-25"
            value={beginValues.year}
            options={getYears()}
            onChange={(e) => {
              selectDate(intervalTypes.CUSTOM_RANGE, {
                begin: { year: e.target.value },
              });
            }}
          />
        </div>

        <div className="d-flex align-center">
          <Select
            label="Dia"
            className="w-100"
            value={beginValues.day}
            options={getDays()}
            onChange={(e) => {
              selectDate(intervalTypes.CUSTOM_RANGE, {
                begin: { day: e.target.value },
              });
            }}
          />
          <If cond={!hideHours}>
            <Select
              label="Hora"
              className="w-100 ml-15"
              value={beginValues.time}
              options={getHoraries()}
              onChange={(e) => {
                selectDate(intervalTypes.CUSTOM_RANGE, {
                  begin: { time: e.target.value },
                });
              }}
            />
          </If>
        </div>

        <Hairline className="mt-30 mb-30" />
        <Label bold className="mb-15">
          Final do intervalo em:
        </Label>

        <div className="d-flex align-center">
          <Select
            label="Mês"
            className="w-100 mb-25"
            value={endValues.month}
            options={getMonths()}
            onChange={(e) => {
              selectDate(intervalTypes.CUSTOM_RANGE, {
                end: { month: e.target.value },
              });
            }}
          />
          <Select
            label="Ano"
            className="w-100 ml-15 mb-25"
            value={endValues.year}
            options={getYears()}
            onChange={(e) => {
              selectDate(intervalTypes.CUSTOM_RANGE, {
                end: { year: e.target.value },
              });
            }}
          />
        </div>

        <div className="d-flex align-center">
          <Select
            label="Dia"
            className="w-100"
            value={endValues.day}
            options={getDays()}
            onChange={(e) => {
              selectDate(intervalTypes.CUSTOM_RANGE, {
                end: { day: e.target.value },
              });
            }}
          />
          <If cond={!hideHours}>
            <Select
              label="Hora"
              className="w-100 ml-15"
              value={endValues.time}
              options={getHoraries()}
              onChange={(e) => {
                selectDate(intervalTypes.CUSTOM_RANGE, {
                  end: { time: e.target.value },
                });
              }}
            />
          </If>
        </div>

        <If cond={errorMessage}>
          <Message type="warning" className="mt-20">
            {errorMessage}
          </Message>
        </If>
      </If>
    </>
  );
}
